import AppLoading from '@/components/AppLoading';
import { PERP_API_URL } from '@/configs';
import { WalletContext } from '@/contexts/wallet-context';
import useApiInfinite from '@/hooks/useApiInfinite';
import useDebounce from '@/hooks/useDebounce';
import KeyIcon from '@/modules/AlphaPWA/Profiles/BuyAKey/KeyIcon';
import Avatar from '@/modules/AlphaPWA/Profiles/TradeKey/components/avatar';
import FieldText from '@/modules/AlphaPWA/Profiles/TradeKey/components/form/fieldText';
import { apiClient } from '@/services';
import { formatName } from '@/utils';
import { getUrlAvatarTwitter } from '@/utils/helpers';
import px2rem from '@/utils/px2rem';
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  Text,
} from '@chakra-ui/react';
import cs from 'classnames';
import React, { useContext, useEffect, useRef } from 'react';
import { Field, useFormState } from 'react-final-form';
import { RxMagnifyingGlass } from 'react-icons/rx';
import InfiniteScroll from 'react-infinite-scroll-component';
import s from './styles.module.scss';

const LIMIT_PAGE = 20;

interface KeysModalProps {
  onItemClick: (data: any) => void;
  isOpen: boolean;
  onClose: () => void;
}

const KeysModal: React.FC<KeysModalProps> = ({
  onItemClick,
  onClose,
  isOpen,
}: KeysModalProps) => {
  const { addressL2 } = useContext(WalletContext);
  const { values } = useFormState();

  const refTokenParams = useRef<any>({});

  const infiniteScrollRef = useRef<any>(null);
  const refHoldingParams = useRef<any>({});
  const debounced = useDebounce(values?.search_text);

  useEffect(() => {
    onSearch(values?.search_text);
  }, [debounced]);

  const onSearch = async (text: string) => {
    refHoldingParams.current = {
      ...refHoldingParams.current,
      search: text?.trim(),
    };
    refTokenParams.current = {
      key_type: 2,
      address: addressL2,
      search: text?.trim(),
    };
    return onFilter();
  };

  const onFilter = () => {
    refresh();
  };

  const getData = async ({ page = 1, limit = 20, search = '' }) => {
    try {
      const data: any[] = await apiClient.get(
        `${PERP_API_URL}/api/player-share/tokens?network=nos&address=&key_type=1&page=${page}&limit=${limit}&search=${search}`
      );
      return data;
    } catch (error) {}
    return [];
  };

  const {
    dataInfinite,
    loadMore,
    refresh,
    isLoadingMore,
    isReachingEnd,
    isRefreshing,
  } = useApiInfinite(
    getData,
    { limit: LIMIT_PAGE, search: values?.search_text || '' },
    { revalidateOnFocus: true }
  );

  const renderLoading = () => (
    <div className={s.loadingMore}>
      <AppLoading />
    </div>
  );

  const renderSearchBar = () => (
    <Box className={s.txtSearchWrapper}>
      <Field
        component={FieldText}
        name="search_text"
        style={{
          textAlign: 'left',
          fontSize: '14px',
        }}
        placeHolder="Search"
        className={s.txtSearch}
        prependComp={
          <Box pt={8} pl={4}>
            <RxMagnifyingGlass fontSize={'24px'} color={'#B6B6B6'} />
          </Box>
        }
        borderColor={'#ECECED'}
      />
    </Box>
  );

  const renderItem = (userKey: any) => {
    return (
      <div
        id={`user_${userKey.user_twitter_id}`}
        onClick={() => {
          onItemClick(userKey);
          onClose();
        }}
      >
        <Flex
          alignItems={'center'}
          justifyContent={'space-between'}
          pb="8px"
          mt="8px"
          borderBottom="0.5px solid #FFFFFF0d"
        >
          <Flex gap={2} alignItems={'center'} maxW={'80%'}>
            <Avatar
              url={getUrlAvatarTwitter(
                (userKey?.user_twitter_avatar || userKey?.avatar) as string,
                'normal',
                Boolean(userKey?.user_twitter_avatar)
              )}
              address={userKey?.owner}
              width={36}
            />
            <Flex gap={1} direction={'column'}>
              <Flex alignItems={'center'} gap={2}>
                <Text className={s.buyer} maxW={200}>
                  {formatName(userKey?.user_twitter_name as string, 50)}
                </Text>
                {/* <Flex
                  className={cs(
                    s.keyContainer,
                    userKey.is_pre_sale && s.keyContainerPresale
                  )}
                >
                  <KeyIcon />
                </Flex> */}
              </Flex>
              <Text
                color={'#000000'}
                fontSize={`${px2rem(14)} !important`}
                opacity={'70%'}
              >
                @{userKey?.user_twitter_username}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </div>
    );
  };

  const renderTabsContent = () => {
    return (
      <div className={s.wrapScroll}>
        <InfiniteScroll
          ref={infiniteScrollRef}
          className="disable-scrollbar-x"
          height="calc(100dvh - 164px)"
          dataLength={dataInfinite?.length || 500}
          hasMore={!isReachingEnd}
          loader={isLoadingMore && renderLoading()}
          refreshFunction={refresh}
          pullDownToRefresh
          pullDownToRefreshThreshold={50}
          next={loadMore}
        >
          {isRefreshing && renderLoading()}
          {(dataInfinite || []).map((item: any) => renderItem(item))}
        </InfiniteScroll>
      </div>
    );
  };

  const renderContent = () => {
    return renderTabsContent();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalContent>
        <ModalCloseButton />
        <ModalBody style={{ overflow: 'hidden' }}>
          {renderSearchBar()}
          {renderContent()}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default KeysModal;
